import * as React from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Checkbox,
  ClickAwayListener,
  FormControl,
  InputAdornment,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Tooltip from "@mui/material/Tooltip";
import "./DepartmentStatusMultiSelect.scss";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";

function uniq(arr) {
  return Array.from(new Set(arr));
}

function DepartmentMultiSelect({
  label = "Department",
  options = [],
  value = [], // applied value (controlled)
  onChange, // (nextAppliedArray) => void
  allLabel = "All",
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [draft, setDraft] = React.useState(value); // selections inside the popper
  const anchorRef = React.useRef(null);

  React.useEffect(() => {
    // when external value changes, sync draft
    setDraft(value);
  }, [value]);

  const filteredOptions = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, search]);

  const isAllSelected = options.length > 0 && draft.length === options.length;

  const displayText = React.useMemo(() => {
    if (!value?.length) return allLabel;
    if (value.length === options.length) return allLabel;
    return value.length === 1 ? value[0] : `${value.length - 1} +`;
  }, [value, options.length, allLabel]);

  const toggleOpen = () => {
    setOpen((s) => !s);
    // reset search when opening
    setSearch("");
    // ensure draft reflects current applied when opening
    setDraft(value);
  };

  const close = () => {
    setOpen(false);
    setSearch("");
    setDraft(value); // discard changes
  };

  const toggleOne = (opt) => {
    setDraft((prev) => {
      const set = new Set(prev);
      if (set.has(opt)) set.delete(opt);
      else set.add(opt);
      return Array.from(set);
    });
    
  };

  const toggleAll = () => {
    setDraft(isAllSelected ? [] : [...options]);
  };

  const clearAll = () => {
    setDraft([]);
  };

  const apply = () => {
    const next = uniq(draft).filter((x) => options.includes(x));
    onChange?.(next);
    setOpen(false);
    setSearch("");
  };

  return (
    <FormControl
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "30px", // rounded pill
          backgroundColor: "#fff",
          "& fieldset": {
            border: "none", // remove border
          },
          "&:hover fieldset": {
            border: "none",
          },
        },
        maxWidth: value?.length > 1 ||  value?.length === filteredOptions.length || value?.length === 0 ?  100 : "fit-content",
      }}
      className="department-multi-select"
    >
      {/* Anchor input (looks like Select) */}
      {(value.length !== options.length && value.length > 1 ) ?
        <Tooltip title={<div>
            {value.map((x, i) => (
              <div key={i + 1}>{x}</div>
            ))}
          </div>} 
          placement="bottom"
        >
          <OutlinedInput
            ref={anchorRef}
            label={label}
            readOnly
            value= {displayText}
            onClick={toggleOpen}
            endAdornment={ 
              <InputAdornment position="end">
                <ExpandMoreIcon
                  sx={{
                    transform: open ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "150ms",
                    cursor: "pointer",
                  }}
                />
              </InputAdornment>
            }
            sx={{
              fontSize: "16px",
              fontWeight: 600,
              color: "#403C6F",
            //   paddingX: "15px",
            //   paddingY: "8px",
              "& .MuiSelect-icon": {
                color: "#403C6F",
                right: "8px",
              },
            }}
            MenuProps={{
              slotProps: {
                paper: {
                  sx: {
                    backgroundColor: "#6E6D92",
                    borderRadius: "12px",
                    color: "#FFFFFF",
                    "& .MuiMenuItem-root": {
                      "&.Mui-selected": {
                        backgroundColor: "#403C6F",
                      },
                      "&:hover": {
                        backgroundColor: "#ffffff",
                      },
                    },
                  },
                },
              },
            }}
          />
        </Tooltip>: 
        <OutlinedInput
          ref={anchorRef}
          label={label}
          readOnly
          value= {displayText}
          onClick={toggleOpen}
          endAdornment={ 
            <InputAdornment position="end">
              <ExpandMoreIcon
                sx={{
                  transform: open ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "150ms",
                  cursor: "pointer",
                }}
              />
            </InputAdornment>
          }
          sx={{
            fontSize: "16px",
            fontWeight: 600,
            color: "#403C6F",
          //   paddingX: "15px",
          //   paddingY: "8px",
            "& .MuiSelect-icon": {
              color: "#403C6F",
              right: "8px",
            },
          }}
          MenuProps={{
            slotProps: {
              paper: {
                sx: {
                  backgroundColor: "#6E6D92",
                  borderRadius: "12px",
                  color: "#FFFFFF",
                  "& .MuiMenuItem-root": {
                    "&.Mui-selected": {
                      backgroundColor: "#403C6F",
                    },
                    "&:hover": {
                      backgroundColor: "#ffffff",
                    },
                  },
                },
              },
            },
          }}
        />}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
        modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      >
        <ClickAwayListener onClickAway={close}>
          <Paper
            elevation={6}
            className="department-multi-select"
            sx={{
              bgcolor: "#6E6D92",
              color: "#ffffff",
              borderRadius: 3,
              overflow: "hidden",
              minWidth: "100%",
            }}
          >
            {/* Search */}
            <Box sx={{ p: 1 }}>
              <TextField
                fullWidth
                placeholder={`Search ${label}`}
                value={search}
                onChange={(e) => {
                  onChange?.([]) // reset applied when searching;
                  setSearch(e.target.value)
                }}
                size="small"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <SearchIcon sx={{ color: "#ffffff" }} />
                      </InputAdornment>
                    ),
                  },
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "#6E6D92",
                    color: "#ffffff",
                    borderRadius: 2,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#ffffff",
                  },
                  "& .MuiInputBase-input::placeholder": {
                    color: "#ffffff",
                    opacity: 1,
                  },
                }}
              />
            </Box>
            <OverlayScrollbarsComponent
              options={{
                scrollbars: {
                  theme: "os-theme-dark",
                  autoHide: "leave",      // better than "scroll" for perceived lag
                  autoHideDelay: 0,  
                },
              }}
              style={{ maxHeight: "500px"}}
            >
              {/* All */}
              {search ?"":
                <MenuItem
                  dense
                  onClick={toggleAll}
                  sx={{
                    color: "#ffffff",
                      padding: "0px 12px",
                    "&:hover": { bgcolor: "#403C6F" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: "normal",
                          maxWidth: "95%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip
                          title={allLabel}
                          placement="top"
                          disableInteractive 
                          enterDelay={600} 
                        >
                          {allLabel}
                        </Tooltip>
                      </Typography>
                    }
                  />
                  <Checkbox
                    checked={isAllSelected}
                    indeterminate={!isAllSelected && draft.length > 0}
                    sx={{
                      color: "#ffffff",
                      "&.Mui-checked": { color: "#ffffff" },
                      "&.MuiCheckbox-indeterminate": { color: "#ffffff" },
                    }}
                  />
                </MenuItem>
              }
              {filteredOptions.map((opt) => (
                <MenuItem
                  key={opt}
                  dense
                  onClick={() => toggleOne(opt)}
                  sx={{
                    color: "#ffffff",
                    padding: "0px 12px",
                    "&:hover": { bgcolor: "#403C6F" },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        sx={{
                          fontWeight: "normal",
                          maxWidth: "95%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Tooltip
                          title={opt}
                          placement="top"
                          disableInteractive 
                          enterDelay={600} 
                        >
                          {opt}
                        </Tooltip>
                      </Typography>
                    }
                  />
                  <Checkbox
                    checked={draft.includes(opt)}
                    sx={{
                      color: "#ffffff",
                      "&.Mui-checked": { color: "#ffffff" },
                    }}
                  />
                </MenuItem>
              ))}

              {!filteredOptions.length && (
                <Box sx={{ p: 2, opacity: 0.9 }}>
                  <Typography>No results</Typography>
                </Box>
              )}
            </OverlayScrollbarsComponent>

            {/* Footer buttons */}
            <Box
              sx={{ p: 1.5, display: "flex", justifyContent: "space-between" }}
            >
              <Button
                onClick={clearAll}
                sx={{
                  bgcolor: "white",
                  color: "#3c3573",
                  fontWeight: "normal",
                  fontFamily: "Inter, sans-serif",
                  borderRadius: 999,
                  fontSize: "12px",
                  px: 2.5,
                    pl: "12px",
                  "&:hover": { bgcolor: "#ffffff" },
                }}
              >
                Clear all
              </Button>

              <Button
                onClick={apply}
                sx={{
                  bgcolor: "#3c3573",
                  color: "#ffffff",
                  fontWeight: "normal",
                  borderRadius: 999,
                  fontSize: "12px",
                  fontFamily: "Inter, sans-serif",
                  px: 3,
                  "&:hover": { bgcolor: "#3c3573" },
                }}
              >
                Apply
              </Button>
            </Box>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
}

DepartmentMultiSelect.propTypes = {
  label: PropTypes.string,
  options: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func,
  allLabel: PropTypes.string,
};

export default DepartmentMultiSelect;
